import React, {
  ChangeEvent,
  useMemo,
  useRef,
  useState,
} from "react";
import PrintIcon from "@mui/icons-material/Print";
import { IPayroll } from "Dashboard/scenes/HumanResources/domain/IPayroll";
import {
  getMonthlyCarnetsForEmployee,
  getMonthlyPayrollForEmployeePrint,
  setDeletePayrollModalOpen,
  updatePayroll,
  updatePayrollForPrint
} from "../payroll.slice";
import { getEmployees, selectState } from "../../employee/employeeSlice";
import { monthNames } from "utils/translationDictionary";
import { useAppDispatch } from "hooks/hooks";
import { hrService } from "Dashboard/scenes/HumanResources/services/hr.services";
import { debounce } from "lodash";
import { IUpdatePayrollForEmployeeRequestParams } from "Dashboard/scenes/HumanResources/domain/IUpdatePayrollForEmployeeRequestParams";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";
import AddIcon from "@mui/icons-material/Add";
import { ExpenseRow } from "./ExpenseRow";
import DeleteIcon from "./../../../../../images/delete-icon.png";
import StarIcon from "./../../../../../images/star.png";
import PrintMonthlyPayroll from "./PrintMonthlyPayroll";
import PrintCarnets from "Dashboard/scenes/Carnets/scenes/Main/components/PrintCarnets";
import { IEmployee } from "Dashboard/scenes/HumanResources/domain/IEmployee";
import InfoTooltip from "components/Popup/InfoTooltip";
import { useSelector } from "react-redux";
import { IGetAllEmployeesRequestParams } from "Dashboard/scenes/HumanResources/domain/IGetAllEmployeesRequestParams";
import { selectState as selectStateAuth } from "authentication/main.slice";
import { hasFullAccessTo, hasWriteAccessTo } from "utils/authUtil";
import { HUMAN_RESOURCES_MODULE_NAME } from "Dashboard/scenes/HumanResources/domain/HumanResourcesModule";
import { usePagePrint } from "hooks/usePagePrint";
import { toast } from "react-toastify";
import { FormatNumberOrNull, FormatNumberWithUnitOrNull } from "utils/numberUtils";
import NumberInput from "Dashboard/components/NumberInput";

interface IProps {
  employee: IEmployee;
  index: number;
  payroll: IPayroll;
}

const PayrollCard = ({ employee, payroll, index }: IProps) => {
  const dispatch = useAppDispatch();

  const [amount, setAmount] = useState<string>();
  const [note, setNote] = useState<string>();

  const componentRefCarnets = useRef<HTMLDivElement>(null);
  const componentRef = useRef<HTMLDivElement>(null);

  let currentDate = new Date();
  let isInFuture = payroll.year >= currentDate.getUTCFullYear()
    && payroll.month > currentDate.getUTCMonth() + 1;
  let firstDayOfTheMonth = new Date(payroll.year, payroll.month - 1, 1);
  let lastDayOfTheMonth = new Date(payroll.year, payroll.month, 0);

  const { getEmployeesParams } = useSelector(selectState);
  const { user } = useSelector(selectStateAuth);

  const handlePrintCarnets = usePagePrint(componentRefCarnets,
    { margins: "1cm 0.3cm" });

  const handlePrintClick = () => {
    dispatch(getMonthlyCarnetsForEmployee({
      employeeId: employee.employeeId,
      from: FormatDateIso(firstDayOfTheMonth),
      to: FormatDateIso(lastDayOfTheMonth)
    }))

    setTimeout(() => {
      handlePrintCarnets();
    }, 200);
  };

  const handlePrint = usePagePrint(componentRef, { margins: "0.5cm" });

  const handlePayrollForPrintClick = () => {
    dispatch(getMonthlyPayrollForEmployeePrint({
      employeeId: employee.employeeId,
      year: payroll.year,
      month: payroll.month
    }))

    setTimeout(() => {
      handlePrint();
    }, 100);
  };

  const updatePayrollForEmployee = useMemo(
    () =>
      debounce(async (
        data: IUpdatePayrollForEmployeeRequestParams,
        getEmployeesParams: IGetAllEmployeesRequestParams
      ) => {
        try {
          const payroll = await hrService.updatePayrollForEmployee(data);
          dispatch(updatePayroll(payroll));
          dispatch(updatePayrollForPrint({
            year: payroll.year,
            month: payroll.month,
            paidOnDate: payroll.payedOnDate ?? undefined,
            paidByBank: payroll.payedByBank,
            travelExpenses: payroll.travelExpenses,
            basicSalary: payroll.basicSalary,
            bonus: payroll.bonus,
            correction: payroll.correction
          }));
          if (data.forcedField === "payedOnDate") {
            console.log("reloading employees", getEmployeesParams);
            dispatch(getEmployees(getEmployeesParams));
          }
        } catch (err) {
          console.log(err);
        }
      }, 1000),
    [dispatch]
  );

  const resetExpenseInputs = () => {
    setAmount("");
    setNote("");
  };

  const addExpense = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (amount && note) {
      try {
        const newPayroll = await hrService.addExpenseToPayrollForEmployee({
          employeeId: employee.employeeId,
          payrollId: payroll.payrollId,
          amount: Number(amount),
          note,
        });
        dispatch(updatePayroll(newPayroll));
        dispatch(updatePayrollForPrint({
          year: payroll.year,
          month: payroll.month,
          paidExpenses: newPayroll.expenses.map(expense => ({
            amount: expense.amount,
            note: expense.note
          }))
        }));
      } catch (err) {
        console.log(err);
      }
      resetExpenseInputs();
    } else {
      toast.warn("Obavezno je uneti i iznos troška i komentar");
    }
  };

  const updateNumberFieldChanged = (
    name: string, value: number
  ) => {
    let newPayroll = { ...payroll, [name]: value };
    dispatch(updatePayroll(newPayroll));
    updatePayrollForEmployee({
      employeeId: employee.employeeId,
      payrollId: payroll.payrollId,
      [name]: value ?? 0,
    }, getEmployeesParams);
  };

  const updateNoteFieldChanged = (
    event: ChangeEvent<HTMLTextAreaElement>
  ) => {
    let newPayroll = { ...payroll, [event.target.name]: event.target.value };
    dispatch(updatePayroll(newPayroll));
    updatePayrollForEmployee({
      employeeId: employee.employeeId,
      payrollId: payroll.payrollId,
      [event.target.name]: event.target.value,
    }, getEmployeesParams);
  };

  const updatePayrollDate = async (date: Date | null) => {

    let payedOnDate: string | null = date ? FormatDateIso(date) : null;
    let newPayroll = { ...payroll, payedOnDate: payedOnDate };
    dispatch(updatePayroll(newPayroll));
    updatePayrollForEmployee({
      employeeId: employee.employeeId,
      payrollId: payroll.payrollId,
      payedOnDate: payedOnDate,
      forcedField: "payedOnDate",
    }, getEmployeesParams);
  };

  return (
    <div className="salary-grid salary-monthly-card" key={index}>
      <div className="text-right margin-for-month-indicator">
        <div>
          obračun
          <InfoTooltip tooltip="Obračun" element="span" classesProp={payroll.payedOnDate ? 'green-txt' : 'red-txt'}>
            {FormatNumberOrNull(payroll.payedByHand)}
          </InfoTooltip>
        </div>
        <div>
          na račun
          <InfoTooltip tooltip="Isplaćeno preko računa" element="span" classesProp="input-tooltip">
            <NumberInput
              formatAs="quantity"
              value={`${payroll.payedByBank}`}
              onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
                ? (value: string, unmaskedValue: string, parsedValue: number) => {
                  if (parsedValue !== payroll.payedByBank) {
                    updateNumberFieldChanged("payedByBank", parsedValue);
                  }
                }
                : undefined}
              className="blue-txt"
              disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
            />
          </InfoTooltip>
        </div>
        <div>
          obroci
          <InfoTooltip tooltip="Troškovi keš obroka" element="span" classesProp="blue-txt">
            {FormatNumberOrNull(payroll.mealEarnings)}
          </InfoTooltip>
        </div>
        <div>
          prevoz
          <InfoTooltip tooltip="Putni troškovi" element="span" classesProp="input-tooltip">
            <NumberInput
              formatAs="quantity"
              value={`${payroll.travelExpenses}`}
              onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
                ? (value: string, unmaskedValue: string, parsedValue: number) => {
                  if (parsedValue !== payroll.travelExpenses) {
                    updateNumberFieldChanged("travelExpenses", parsedValue);
                  }
                }
                : undefined}
              className="blue-txt"
              disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
            />
          </InfoTooltip>
        </div>
        <div>
          <label>osnovna zarada</label>
          {employee.salaryType === 'MONTHLY' &&
            <InfoTooltip tooltip="Suma zarada" element="span" classesProp="input-tooltip">
              <NumberInput
                formatAs="quantity"
                value={`${payroll.basicSalary}`}
                onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
                  ? (value: string, unmaskedValue: string, parsedValue: number) => {
                    if (parsedValue !== payroll.basicSalary) {
                      updateNumberFieldChanged("basicSalary", parsedValue);
                    }
                  }
                  : undefined}
                className="blue-txt"
                disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
              />
            </InfoTooltip>
          }
          {employee.salaryType === 'HOURLY' &&
            <InfoTooltip tooltip="Suma zarada po satu"
              element="span" classesProp="blue-txt">
              {FormatNumberOrNull(payroll.basicSalary)}
            </InfoTooltip>}
        </div>
        <div>
          <label>ukupno zaradio</label>
          <InfoTooltip tooltip="obračun + na račun + prevoz + troškovi"
            element="span" classesProp="blue-txt">
            {FormatNumberOrNull(payroll.totalAmountEarned)}
          </InfoTooltip>
        </div>
      </div>
      <div className="datePicker">
        <InfoTooltip tooltip="Datum isplate">
          <DatePicker
            dateFormat="dd.MM.yyyy"
            onChange={(date: any) => {
              updatePayrollDate(date);
            }}
            showMonthDropdown
            showYearDropdown
            selected={payroll.payedOnDate ? new Date(payroll.payedOnDate) : null}
            disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
          />
          {payroll.payedOnDate === null &&
            <img className="star" src={StarIcon} alt="" />}
          {/* <span>21.5.2021.</span> */}
        </InfoTooltip>
      </div>
      <div className="carnet-column">
        {payroll.hourlyWork.map((item, index) => (
          <div key={index}>
            {item.label.toLowerCase()}<span>{FormatNumberOrNull(item.hours)}</span>
          </div>
        ))}
        <div>
          bolovanja <span>{FormatNumberWithUnitOrNull(payroll.sickLeaveDays, 'd')} ({FormatNumberWithUnitOrNull(payroll.sickLeaveDaysValue, 'rsd', { maximumFractionDigits: 0 })})</span>
        </div>
        <div>
          odmor <span>{FormatNumberWithUnitOrNull(payroll.vacationDays, 'd')} ({FormatNumberWithUnitOrNull(payroll.vacationDaysValue, 'rsd', { maximumFractionDigits: 0 })})</span>
        </div>
        <div>
          efektivno{" "}
          <span className="purple">{FormatNumberWithUnitOrNull(payroll.effectiveHours, 'sati')}</span>
        </div>
        <div>
          efektivno{" "}
          <span className="purple">{FormatNumberWithUnitOrNull(payroll.effectiveKilometers, 'km')}</span>
        </div>
      </div>
      <div>
        <InfoTooltip tooltip="Štampaj karnet">
          <button onClick={handlePrintClick} className="printIcon-fix">
            <PrintIcon className="fas" fontSize="small" />
          </button>
        </InfoTooltip>
      </div>
      <div className="locked-width-105">
        <div>
          doručak <span>{FormatNumberOrNull(payroll.regularBreakfasts)}</span>
        </div>
        <div>
          ručak <span>{FormatNumberOrNull(payroll.regularLunches)}</span>
        </div>
        <div>
          večera <span>{FormatNumberOrNull(payroll.regularDinners)}</span>
        </div>
        <div>
          keš doručak <span>{FormatNumberOrNull(payroll.cashBreakfasts)}</span>
        </div>
        <div>
          keš ručak <span>{FormatNumberOrNull(payroll.cashLunches)}</span>
        </div>
        <div>
          keš večera <span>{FormatNumberOrNull(payroll.cashDinners)}</span>
        </div>
      </div>
      <div className="text-right">
        <div>
          bonus
          <InfoTooltip tooltip="Bonus" element="span" classesProp="input-tooltip">
            <NumberInput
              formatAs="quantity"
              value={`${payroll.bonus}`}
              onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
                ? (value: string, unmaskedValue: string, parsedValue: number) => {
                  if (parsedValue !== payroll.bonus) {
                    updateNumberFieldChanged("bonus", parsedValue);
                  }
                }
                : undefined}
              disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
            />
          </InfoTooltip>
        </div>
        <div>
          korekcija{" "}
          <InfoTooltip tooltip="Korekcija" element="span" classesProp="input-tooltip">
            <NumberInput
              formatAs="quantity"
              value={`${payroll.correction}`}
              onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
                ? (value: string, unmaskedValue: string, parsedValue: number) => {
                  if (parsedValue !== payroll.correction) {
                    updateNumberFieldChanged("correction", parsedValue);
                  }
                }
                : undefined}
              disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
            />
          </InfoTooltip>
        </div>
      </div>
      <div>
        {payroll.expenses &&
          payroll.expenses.map((expense, eindex) => (
            <ExpenseRow
              employeeId={employee.employeeId}
              payrollId={payroll.payrollId}
              expense={expense}
              key={eindex}
            />
          ))}
        {hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
          ? <div className="locked-width-150">
            <NumberInput
              formatAs="quantity"
              placeholder="iznos"
              value={`${amount}`}
              onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
                ? (value: string, unmaskedValue: string, parsedValue: number) => {
                  if (unmaskedValue !== amount) {
                    setAmount(unmaskedValue);
                  }
                }
                : undefined}
              disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
            />
            <input
              type="text"
              placeholder="komentar"
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            ></input>
            <button onClick={addExpense}>
              <AddIcon fontSize="small" />
            </button>
          </div>
          : <></>}
        <textarea
          className="textarea-150"
          name="note"
          onChange={hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME) ? updateNoteFieldChanged : undefined}
          value={payroll.note}
          disabled={!hasWriteAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)}
        ></textarea>
      </div>
      <div>
        <InfoTooltip tooltip="Štampaj priznanicu" element="span" classesProp="button-tooltip">
          <button onClick={handlePayrollForPrintClick}>
            <PrintIcon className="fas" fontSize="small" />
          </button>
        </InfoTooltip>
        {hasFullAccessTo(user, HUMAN_RESOURCES_MODULE_NAME)
          ? <InfoTooltip tooltip="Obriši zapis" element="span" classesProp="button-tooltip">
            <button
              onClick={() => {
                dispatch(setDeletePayrollModalOpen({ visible: true, index }));
              }}
            >
              <img src={DeleteIcon} alt="" />
              {/* <CloseIcon className="fas" fontSize="small" /> */}
            </button>
          </InfoTooltip>
          : <></>}
      </div>
      <div className="month-indicator">
        <span className="year">{payroll.year}</span>
        <span className="month">{monthNames[payroll.month - 1]}</span>
        {isInFuture &&
          <span className="is-future">u budućnosti</span>
        }
      </div>
      <div ref={componentRefCarnets} className="print-area">
        <PrintCarnets year={payroll.year} month={monthNames[payroll.month - 1]} />
      </div>
      <div ref={componentRef} className="print-area">
        <PrintMonthlyPayroll />
      </div>
    </div>
  );
};

export default PayrollCard;
